import React, { useMemo, useState, useEffect } from "react";
import styles from "./Dashboard.module.scss";
import {
  FiMonitor,
  FiClock,
  FiMail,
  FiMessageCircle,
  FiSearch,
  FiGrid,
  FiCalendar,
  FiSend,
  FiArchive,
} from "react-icons/fi";
import Header from "../Header/header";
import QrModal from "../Dashboard/QrModal";
import axios from "axios";
import TutorialOverlay from "./TutorialOverlay";  
import Tooltip from "@mui/material/Tooltip";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

// ---------- Types ----------
type ConsentStatus = "EMAIL_BOUNCED" | "NOT_SIGNED" | "SIGNED";
type TabKey = "today" | "reminders" | "upload";

type Appointment = {
  id: string;
  time: string;
  date?: string;
  patient: string;
  clinician: string;
  consentStatus: "EMAIL_BOUNCED" | "NOT_SIGNED" | "SIGNED";
  emails: number;
  texts: number;
  reminders?: number;
  lastReminder?: string | null;
  daysUntilAppointment?: number; 
} & Record<string, any>;

type UploadRow = {
  id: string;
  patient: string;
  appointmentDate: string; // YYYY-MM-DD
  clinician: string;
  consentState: "SIGNED" | "PENDING";
  status: string; // keep backend text ("Completed", "Draft", etc.)
  email: number;
  sms: number;
  signedDate: string | null; // YYYY-MM-DD or null
  raw: Record<string, any>; // preserve everything from API
};

// ---------- Data (keep for other tabs only) ----------
const REMINDER_APPTS: Appointment[] = [
];

const UPLOAD_ROWS: UploadRow[] = [
  { id: "u1", patient: "Michael Chen", appointmentDate: "2025-01-10", clinician: "Dr Brown", consentState: "SIGNED",  status: "COMPLETED",     email: 1, sms: 1, signedDate: "2025-12-21", raw: {} },
  { id: "u2", patient: "Robert Davis",  appointmentDate: "2025-01-12", clinician: "Dr Brown", consentState: "SIGNED",  status: "COMPLETED",     email: 1, sms: 1, signedDate: "2025-12-17", raw: {} },
  { id: "u3", patient: "Robert Davis",  appointmentDate: "2025-01-18", clinician: "Dr Brown", consentState: "PENDING", status: "SENT_UNSIGNED", email: 1, sms: 1, signedDate: null,        raw: {} },
  { id: "u4", patient: "Michael Chen",  appointmentDate: "2025-01-20", clinician: "Dr Brown", consentState: "PENDING", status: "SENT_UNSIGNED", email: 1, sms: 1, signedDate: null,        raw: {} },
];

// ---------- Helpers ----------
const fmtLong = new Intl.DateTimeFormat("en-GB", {
  weekday: "long", month: "long", day: "numeric", year: "numeric",
}).format;

const fmtShort = (d: Date) =>
  new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }).format(d);

const daysUntil = (iso: string) => {
  const today = new Date();
  const target = new Date(iso + "T00:00:00");
  const ms = target.getTime() - new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
};

const todayLong = fmtLong(new Date());

// "2025-09-22 12:05" -> "12:05"
const hhmm = (dt?: string | null) => {
  if (!dt) return "00:00";
  const fixed = dt.includes("T") ? dt : dt.replace(" ", "T");
  const d = new Date(fixed);
  if (isNaN(d.getTime())) return "00:00";
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${h}:${m}`;
};

const onlyDate = (dt?: string | null) => {
  if (!dt) return undefined;
  const t = dt.includes("T") ? dt.split("T")[0] : dt.split(" ")[0];
  return t || undefined;
};

const getUploadStatusBadgeClass = (statusText: string, uploaded: boolean) => {
  const s = (statusText || "").toLowerCase().trim();
  if (uploaded) return "badgeSuccessSoft";
  if (s.includes("draft")) return "badgeGray";
  if (s.includes("sign")) return "badgeYellow";
  if (s.includes("completed")) return "badgeSuccessSoft";
  if (s.includes("unsigned") || s.includes("pending")) return "badgeWarn";
  return "badgeGray";
};

// map for waiting-room + future-appointments
const toAppointment = (raw: any, idx: number): Appointment => {
  const id = String(raw.id ?? raw.appointment_id ?? raw.uuid ?? `wr-${Date.now()}-${idx}`);
  const time = raw.appointment_time ?? hhmm(raw.next_appointment_date) ?? "00:00";
  const patient =
    (raw.patient?.first_name && raw.patient?.last_name)
      ? `${raw.patient.first_name} ${raw.patient.last_name}`
      : raw.patient_name ?? raw.patient ?? "Unknown Patient";
  const clinician = raw.clinician_name ?? raw.clinician ?? "Unknown";

  const consentRaw = String(raw.consent_status ?? raw.status ?? "").toUpperCase();
  let consentStatus: ConsentStatus = "NOT_SIGNED";
  if (consentRaw.includes("BOUNCE")) consentStatus = "EMAIL_BOUNCED";
  else if (consentRaw.includes("SIGNED")) consentStatus = "SIGNED";

  const emails = Number(raw.email_sent_count ?? raw.emails_count ?? 0);
  const texts  = Number(raw.sms_sent_count   ?? raw.sms_count    ?? 0);

  const date =
    raw.next_appointment_date
      ? (raw.next_appointment_date.split("T")[0] || raw.next_appointment_date.split(" ")[0])
      : undefined;

  const reminders    = Number(raw.no_of_reminders_sent ?? 0);
  const lastReminder = raw.last_reminder_sent_at
    ? (raw.last_reminder_sent_at.split("T")[0] || raw.last_reminder_sent_at.split(" ")[0])
    : null;

  const daysUntilAppointment =
    typeof raw.days_until_appointment === "number" ? raw.days_until_appointment : undefined;

  return {
    ...raw, // keep every backend field
    id,
    time,
    date,
    patient,
    clinician,
    consentStatus,
    emails,
    texts,
    reminders,
    lastReminder,
    daysUntilAppointment,
  };
};

const toUploadRow = (raw: any, idx: number): UploadRow => {
  const id = String(raw.id ?? raw.treatment_plan_id ?? raw.uuid ?? `up-${Date.now()}-${idx}`);
  const patient =
    (raw?.patient?.first_name && raw?.patient?.last_name)
      ? `${raw.patient.first_name} ${raw.patient.last_name}`
      : raw.patient_name ?? raw.patient ?? "Unknown Patient";

  let appointmentDate: string | undefined;
  if (raw.treatment_appointment_dates) {
    try {
      const arr = JSON.parse(String(raw.treatment_appointment_dates));
      if (Array.isArray(arr) && arr.length > 0) {
        appointmentDate = onlyDate(arr[0]);
      }
    } catch {
      appointmentDate = onlyDate(String(raw.treatment_appointment_dates));
    }
  }
  if (!appointmentDate) appointmentDate = onlyDate(raw.next_appointment_date) ?? "1970-01-01";

  const clinician = raw.clinician_name ?? raw.clinician ?? "Unknown";
  const signedDate = raw.signed_date ? onlyDate(raw.signed_date) ?? null : null;
  const consentState: UploadRow["consentState"] = signedDate ? "SIGNED" : "PENDING";

  const status: string = String(raw.status ?? (raw.uploaded_to_dentally_patient_record ? "Completed" : "Pending"));

  const email = Number(raw.email_sent_count ?? raw.emails_count ?? 0);
  const sms   = Number(raw.sms_sent_count   ?? raw.sms_count    ?? 0);

  return {
    id,
    patient,
    appointmentDate,
    clinician,
    consentState,
    status,
    email,
    sms,
    signedDate,
    raw: { ...raw },
  };
};

// ---------- Component ----------
const Dashboard: React.FC = () => {
  const [clinician, setClinician] = useState("all"); 
  const [consent, setConsent] = useState("all");
  const [query, setQuery] = useState("");
  const [activeTab, setActiveTab] = useState<TabKey>("today");


  const [showTutorial, setShowTutorial] = useState(true);
  const steps = [
  { selector: ".tutorial-filters", text: "Filter by clinician and patient search here." },
  { selector: ".tutorial-tabs", text: "Switch tabs between Waiting Room, Reminders, and Upload." },
  { selector: ".tutorial-table", text: "Here’s where you see appointments and actions." },
   { selector: ".tutorial-qr", text: "Click here to open the QR Code for this appointment." }
];

  // QR modal state
  const [qrOpen, setQrOpen] = useState(false);
  const [selectedAppt, setSelectedAppt] = useState<any>(null);

  // Archive confirmation state
  const [confirmArchive, setConfirmArchive] = useState<{
    open: boolean;
    id?: string | number;
    name?: string;
    source: "upload" | "reminders" | null;
    loading: boolean;
  }>({ open: false, source: null, loading: false });

  // selections per tab
  const [reminderSelected, setReminderSelected] = useState<Set<string>>(new Set());
  const [uploadSelected, setUploadSelected] = useState<Set<string>>(new Set());

  // Today
  const [todayAppts, setTodayAppts] = useState<Appointment[]>([]);
  const [loadingToday, setLoadingToday] = useState<boolean>(false);
  const [errorToday, setErrorToday] = useState<string | null>(null);

  // Reminders
  const [reminderAppts, setReminderAppts] = useState<Appointment[]>([]);
  const [loadingRem, setLoadingRem] = useState<boolean>(false);
  const [errorRem, setErrorRem] = useState<string | null>(null);
  const [totalRem, setTotalRem] = useState<number>(0);

  // Upload
  const [uploadRows, setUploadRows] = useState<UploadRow[]>([]);
  const [loadingUpload, setLoadingUpload] = useState<boolean>(false);
  const [errorUpload, setErrorUpload] = useState<string | null>(null);
  const [totalUpload, setTotalUpload] = useState<number>(0);

  // shared pagination
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(25);
  const [total, setTotal] = useState<number>(0);

  // upload tab pagination
  const [pageUpload, setPageUpload] = useState<number>(1);
  const [perPageUpload, setPerPageUpload] = useState<number>(25);

  // user + clinicians
  const [user, setUser] = React.useState<any>(null);
  const [clinicians, setClinicians] = React.useState<Array<{ value: string; label: string }>>([]);

  const consentParam = (c: string | undefined) => {
    if (c === "signed") return "SIGNED";
    if (c === "not_signed") return "NOT_SIGNED";
    if (c === "email_bounced") return "EMAIL_BOUNCED";
    return undefined;
  };

  // Waiting room call
  useEffect(() => {
    const fetchData = async () => {
      setLoadingToday(true);
      setErrorToday(null);
      try {
        const params: Record<string, any> = {
          page,
          per_page: perPage,
        };
        if (clinician !== "all") params.clinician = clinician;

        const cs = consentParam(consent);
        if (cs) params.consent_status = cs;

        const resp = await axios.get("https://dev.api.medicube.health/api/v2/dashboard/waiting-room", {
          params,
          headers: { Accept: "application/json" },
        });

        const payload = Array.isArray(resp.data?.data)
          ? resp.data.data
          : Array.isArray(resp.data)
          ? resp.data
          : [];

        const normalized = (payload as any[]).map((r, i) => toAppointment(r, i));
        const dedup = Array.from(new Map(normalized.map(a => [a.id, a])).values());
        setTodayAppts(dedup);
        setTotal(Number(resp.data?.meta?.total ?? dedup.length));
      } catch (e: any) {
        console.error(e);
        setErrorToday(e?.response?.data?.detail ?? e?.message ?? "Failed to load waiting room");
      } finally {
        setLoadingToday(false);
      }
    };

    fetchData();
  }, [page, perPage, clinician, consent]);

  // Future appointments call
  useEffect(() => {
    const fetchReminders = async () => {
      setLoadingRem(true);
      setErrorRem(null);
      try {
        const params: Record<string, any> = {
          page,
          per_page: perPage,
        };
        if (clinician !== "all") params.clinician = clinician;

        const resp = await axios.get(
          "https://dev.api.medicube.health/api/v2/dashboard/future-appointments",
          {
            params,
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          }
        );

        const payload = Array.isArray(resp.data?.data)
          ? resp.data.data
          : Array.isArray(resp.data)
          ? resp.data
          : [];

        const normalized = (payload as any[]).map((r, i) => toAppointment(r, i));
        const dedup = Array.from(new Map(normalized.map(a => [a.id, a])).values());
        setReminderAppts(dedup);
        setTotalRem(Number(resp.data?.meta?.total ?? dedup.length));
      } catch (e: any) {
        console.error(e);
        setErrorRem(e?.response?.data?.detail ?? e?.message ?? "Failed to load future appointments");
      } finally {
        setLoadingRem(false);
      }
    };

    if (activeTab === "reminders") {
      fetchReminders();
    }
  }, [activeTab, page, perPage, clinician]);

  // Ready for upload call
  useEffect(() => {
    const fetchUpload = async () => {
      setLoadingUpload(true);
      setErrorUpload(null);
      try {
        const params: Record<string, any> = {
          page: pageUpload,
          per_page: perPageUpload,
        };
        if (clinician !== "all") params.clinician = clinician;

        const resp = await axios.get(
          "https://dev.api.medicube.health/api/v2/dashboard/ready-for-upload",
          {
            params,
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          }
        );

        const payload = Array.isArray(resp.data?.data)
          ? resp.data.data
          : Array.isArray(resp.data)
          ? resp.data
          : [];

        const normalized = (payload as any[]).map((r, i) => toUploadRow(r, i));
        const dedup = Array.from(new Map(normalized.map(a => [a.id, a])).values());

        setUploadRows(dedup);
        setTotalUpload(Number(resp.data?.meta?.total ?? dedup.length));
      } catch (e: any) {
        console.error(e);
        setErrorUpload(e?.response?.data?.detail ?? e?.message ?? "Failed to load ready-for-upload");
      } finally {
        setLoadingUpload(false);
      }
    };

    if (activeTab === "upload") fetchUpload();
  }, [activeTab, pageUpload, perPageUpload, clinician]);

  // get user + clinicians
  useEffect(() => {
    const getUserData = async () => {
      try {
        const { data } = await axios.get("/user", { headers: { Accept: "application/json" } });

        const u =
          Array.isArray(data)
            ? (typeof data[1] === "object" && data[1] !== null ? data[1] : data[0])
            : data;

        setUser(u);

        if (u?.medicube_practice_id) {
          await getClinicians(u.medicube_practice_id);
        } else {
          console.warn("No medicube_practice_id on user payload:", u);
        }

        return u;
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    const getClinicians = async (practiceId: number) => {
      try {
        const { data } = await axios.get("/users/dropdown/clinicians", {
          params: { practice: practiceId },
          headers: { Accept: "application/json" },
        });

        const arr = Array.isArray(data) ? data : [];
        setClinicians(arr.map((c: any) => ({ value: String(c.value), label: String(c.label) })));
        return arr;
      } catch (error) {
        console.error("Error fetching clinicians:", error);
      }
    };

    getUserData();
  }, []);

  // ---- Filters for Today ----
  const todayFiltered = useMemo(() => {
    return todayAppts.filter((a) => {
      const csOk =
        consent === "all" ||
        (consent === "signed" && a.consentStatus === "SIGNED") ||
        (consent === "not_signed" && a.consentStatus === "NOT_SIGNED") ||
        (consent === "email_bounced" && a.consentStatus === "EMAIL_BOUNCED");

      const qOk =
        !query ||
        a.patient.toLowerCase().includes(query.toLowerCase()) ||
        a.clinician.toLowerCase().includes(query.toLowerCase());

      return csOk && qOk;
    });
  }, [consent, query, todayAppts]);

  // ---- Filters for Reminders ----
  const remindersFiltered = useMemo(() => {
    const rows = (reminderAppts.length ? reminderAppts : REMINDER_APPTS).map((a) => ({
      ...a,
      _days: a.date ? daysUntil(a.date) : 0,
    }));
    return rows.filter((a) => {
      const qOk =
        !query ||
        a.patient.toLowerCase().includes(query.toLowerCase()) ||
        a.clinician.toLowerCase().includes(query.toLowerCase());
      const csOk = consent === "all" ? a.consentStatus === "NOT_SIGNED" : true;
      return qOk && csOk;
    });
  }, [reminderAppts, query, consent]);

  // ---- Filters for Upload ----
  const uploadFiltered = useMemo(() => {
    const rows = uploadRows.length ? uploadRows : UPLOAD_ROWS;
    return rows.filter((r) => {
      const clOk = clinician === "all" || r.clinician === clinician;
      const qOk =
        !query ||
        r.patient.toLowerCase().includes(query.toLowerCase()) ||
        r.clinician.toLowerCase().includes(query.toLowerCase());
      return clOk && qOk;
    });
  }, [uploadRows, clinician, query]);

  // ---- Select helpers ----
  const toggleReminderRow = (id: string) =>
    setReminderSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const toggleReminderAll = () => {
    const allIds = remindersFiltered.map((r) => r.id);
    const allSelected = allIds.every((id) => reminderSelected.has(id));
    setReminderSelected(allSelected ? new Set() : new Set(allIds));
  };

  const toggleUploadRow = (id: string) =>
    setUploadSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const toggleUploadAll = () => {
    const allIds = uploadFiltered.map((r) => r.id);
    const allSelected = allIds.every((id) => uploadSelected.has(id));
    setUploadSelected(allSelected ? new Set() : new Set(allIds));
  };

  const openQr = (a: Appointment) => {
    setSelectedAppt(a);
    setQrOpen(true);
  };

  // ---- Archive helpers ----
  const askArchive = (id: string | number, name: string, source: "upload" | "reminders") => {
    setConfirmArchive({ open: true, id, name, source, loading: false });
  };

  const doArchive = async () => {
  if (!confirmArchive.id) return;

  try {
    setConfirmArchive((p) => ({ ...p, loading: true }));

    await axios.delete(
      `/consent_bundle/${confirmArchive.id}/archive_consent_bundle`,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );

    // Optimistic remove from current list
    if (confirmArchive.source === "upload") {
      setUploadRows((prev) =>
        prev.filter((r) => String(r.raw?.id ?? r.id) !== String(confirmArchive.id))
      );
    } else if (confirmArchive.source === "reminders") {
      setReminderAppts((prev) =>
        prev.filter((r) => String(r.id) !== String(confirmArchive.id))
      );
    }

    showNotify("success", "Archived successfully!");
  } catch (e: any) {
    console.error("Archive failed", e);
    showNotify("error", e?.response?.data?.detail ?? "Failed to archive bundle");
  } finally {
    setConfirmArchive({ open: false, source: null, loading: false });
  }
};


  // ---------- Renders ----------
  const Tabs = (
    <div className={`${styles.tabs} tutorial-tabs`}>
      <button
        className={`${styles.tab} ${activeTab === "today" ? styles.tabActive : ""}`}
        onClick={() => setActiveTab("today")}
      >
        Due today
      </button>
      <button
        className={`${styles.tab} ${activeTab === "reminders" ? styles.tabActive : ""}`}
        onClick={() => setActiveTab("reminders")}
      >
        Send reminder
      </button>
      <button
        className={`${styles.tab} ${activeTab === "upload" ? styles.tabActive : ""}`}
        onClick={() => setActiveTab("upload")}
      >
        Ready for upload
      </button>
    </div>
  );


// ---- Bulk reminder sender ----
const doBulkReminders = async () => {
  if (reminderSelected.size === 0) return;

  try {
    // Find selected rows in reminderAppts and extract consent bundle IDs
    const ids = reminderAppts
      .filter((a) => reminderSelected.has(a.id))
      .map((a) => Number(a.raw?.id ?? a.id)); // fallback if raw.id missing

    if (ids.length === 0) {
      alert("No valid consent bundle IDs found.");
      return;
    }

    await axios.post(
      "https://dev.api.medicube.health/api/v2/dashboard/bulk_send_reminders",
      { consent_bundle_ids: ids },
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );

    // Clear selection after success
    setReminderSelected(new Set());
    alert("Reminders sent successfully!");
  } catch (e: any) {
    console.error("Bulk reminders failed", e);
    alert(e?.response?.data?.detail ?? "Failed to send reminders");
  }
};

const sendReminders = async (ids: number[]) => {
  if (!ids.length) return;

  try {
    await axios.post(
      "https://dev.api.medicube.health/api/v2/dashboard/bulk_send_reminders",
      { consent_bundle_ids: ids },
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );
    alert("Reminders sent successfully!");
  } catch (e: any) {
    console.error("Send reminders failed", e);
    alert(e?.response?.data?.detail ?? "Failed to send reminders");
  }
};


const doBulkDownload = async () => {
  if (uploadSelected.size === 0) return;
  try {
    const ids = uploadFiltered
      .filter((u) => uploadSelected.has(u.id))
      .map((u) => Number(u.raw?.id ?? u.id));

    if (!ids.length) {
      showNotify("error", "No valid consent bundle IDs found.");
      return;
    }

    // Start downloading animation
    showNotify("download", "Downloading...");

    const resp = await axios.post(
      "https://dev.api.medicube.health/api/v2/dashboard/bulk_download",
      { consent_bundle_ids: ids },
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        responseType: "blob",
      }
    );

    const url = window.URL.createObjectURL(new Blob([resp.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "consent_bundles.zip");
    document.body.appendChild(link);
    link.click();
    link.remove();

    setUploadSelected(new Set());

    // Success notification
    showNotify("success", "Downloaded successfully!");
  } catch (e: any) {
    console.error("Bulk download failed", e);
    showNotify("error", e?.response?.data?.detail ?? "Failed to download bundles");
  }
};

const doBulkArchiveUpload = async () => {
  if (uploadSelected.size === 0) return;
  try {
    const ids = uploadFiltered
      .filter((u) => uploadSelected.has(u.id))
      .map((u) => Number(u.raw?.id ?? u.id));

    if (!ids.length) {
      showNotify("error", "No valid consent bundle IDs found.");
      return;
    }

    showNotify("archive", "Archiving...");

    await axios.put(
      "https://dev.api.medicube.health/api/v2/dashboard/bulk_archive",
      { consent_bundle_ids: ids },
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );

    setUploadRows((prev) =>
      prev.filter((r) => !ids.includes(Number(r.raw?.id ?? r.id)))
    );

    setUploadSelected(new Set());

    showNotify("success", "Archived successfully!");
  } catch (e: any) {
    console.error("Bulk archive failed", e);
    showNotify("error", e?.response?.data?.detail ?? "Failed to archive bundles");
  }
};

// ---- Notification system ----
type NotifyType = "archive" | "download" | "success" | "error";

const [notify, setNotify] = useState<{
  show: boolean;
  type: NotifyType;
  message: string;
}>({
  show: false,
  type: "success",
  message: "",
});

const showNotify = (type: NotifyType, msg: string) => {
  setNotify({ show: true, type, message: msg });
  setTimeout(() => setNotify({ show: false, type: "success", message: "" }), 2000);
};;

  return (
    <>
      <Header />
      <div className={styles.wrap}>
        {/* Page Header (varies by tab) */}
        {activeTab === "reminders" ? (
          <header className={styles.header}>
            <div>
              <h1 className={styles.title}>Future appointment</h1>
              <div className={styles.subhead}>
                <FiCalendar />
                <span>Up and coming appointments requiring attention</span>
              </div>
            </div>
          </header>
        ) : activeTab === "upload" ? (
          <header className={styles.header}>
            <div>
              <h1 className={styles.title}>Ready for upload</h1>
              <div className={styles.subhead}>
                <FiCalendar />
                <span>Completed and past appointments</span>
              </div>
            </div>
          </header>
        ) : (
          <header className={styles.header}>
            <div>
              <h1 className={styles.title}>Waiting room</h1>
              <div className={styles.date}>
                <FiMonitor />
                <span>{todayLong}</span>
              </div>
            </div>
            <div className={styles.total}>
              <span>Total appointments</span>
              <strong>{todayAppts.length}</strong>
            </div>
          </header>
        )}

        {/* Filters */}
      <section className={`${styles.filters} tutorial-filters`}>
          <h3 className={styles.filtersTitle}>Filters</h3>

          <div className={styles.filtersRow}>
            <div className={styles.filter}>
              <label>Clinician</label>
              <div className={styles.select}>
                <select
                  value={clinician}
                  onChange={(e) => {
                    setPage(1);
                    if (activeTab === "upload") setPageUpload(1);
                    setClinician(e.target.value);
                  }}
                >
                  <option value="all">All clinicians</option>
                  {clinicians.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
                <span className={styles.selectCaret}>▾</span>
              </div>
            </div>

            {activeTab === "upload" && (
              <div className={styles.filter}>
                <label>Status</label>
                <div className={styles.select}>
                  <select defaultValue="all">
                    <option value="all">All status</option>
                    <option value="completed">Completed</option>
                    <option value="sent">Sent & unsigned</option>
                  </select>
                  <span className={styles.selectCaret}>▾</span>
                </div>
              </div>
            )}

            {/* <div className={styles.search}>
              <FiSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Patient search by name"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  if (activeTab === "upload") setPageUpload(1);
                }}
              />
              <button className={`${styles.btn} ${styles.btnGhost}`}>Search</button>
            </div> */}


            <div className={styles.filter}>
            <FiSearch className={styles.searchIcon} />

            <Autocomplete
              value={query || null}
              onChange={(event, newValue) => {
                setQuery(newValue ? newValue.patientName || newValue : "");
                if (activeTab === "upload") setPageUpload(1);
              }}
              onInputChange={(event, newInputValue) => {
                setQuery(newInputValue);
                if (activeTab === "upload") setPageUpload(1);
              }}
              inputValue={query}
              options={Array.from(
                new Map(
                  [...todayAppts, ...reminderAppts, ...uploadRows].map((a) => [
                    a.patient,
                    { patientName: a.patient },
                  ])
                ).values()
              )}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option.patientName
              }
              noOptionsText="No patients found"
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select a patient"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    style: {
                      background: "#fff",
                      border: "1px solid #e2e2e2", 
                      borderRadius: "8px", 
                      fontWeight: 500,
                      paddingLeft: "36px", 
                      height: "40px",
                      width: "290px",
                      boxShadow: "none",
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        border: "none",
                      },
                      "&:hover fieldset": {
                        border: "1px solid #14156eff",
                      },
                      "&.Mui-focused fieldset": {
                        border: "1px solid #ffffffff",
                        boxShadow: "0 0 0 3px rgba(255, 255, 255, 0.1)",
                      },
                    },
                    "& input::placeholder": {
                      color: "#000000ff",
                      opacity: 1,
                    },
                  }}
                />
              )}
            />
          </div>

            
          </div>
        </section>

        {/* Card + Tabs */}
        <section className={styles.card}>
          {Tabs}

          {/* Wrap tab content to animate on tab change */}
          <div key={activeTab} className={styles.animIn}>

            {/* ---- Today / Waiting room ---- */}
            {activeTab === "today" && (
              <>
                <h4 className={styles.sectionTitle}>Today's appointment</h4>

               <div className={`${styles.table} tutorial-table`}>
                  <div className={`${styles.grid} ${styles.tableHead}`}>
                    <div>Time</div>
                    <div>Patient name</div>
                    <div>Clinician</div>
                    <div>Consent status</div>
                    <div>Emails</div>
                   
                    <div>Action</div>
                  </div>

                  <div className={styles.tableBody}>
                    {loadingToday && (
                      <div className={styles.row}>
                        <div className={styles.cell} style={{ gridColumn: "1 / -1" }}>
                          Loading waiting room…
                        </div>
                      </div>
                    )}

                    {errorToday && !loadingToday && (
                      <div className={styles.row}>
                        <div className={styles.cell} style={{ gridColumn: "1 / -1", color: "crimson" }}>
                          {errorToday}
                        </div>
                      </div>
                    )}

                    {!loadingToday && !errorToday && todayFiltered.length === 0 && (
                      <div className={styles.row}>
                        <div className={styles.cell} style={{ gridColumn: "1 / -1" }}>
                          No appointments found.
                        </div>
                      </div>
                    )}

                    {!loadingToday && !errorToday && todayFiltered.map((a) => {
                      const rowState =
                        a.consentStatus === "EMAIL_BOUNCED"
                          ? styles.rowBounced
                          : a.consentStatus === "NOT_SIGNED"
                          ? styles.rowNotSigned
                          : "";
                      return (
                        <div className={`${styles.row} ${rowState} ${styles.grid}`} key={a.id}>
                          <div className={styles.cell}>
                            <span className={styles.time}><FiClock />{a.time}</span>
                          </div>

                          <div className={styles.cell}>
                            <div className={styles.patient}>
                              <span className={styles.avatar}>
                                {a.patient.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()}
                              </span>
                              <span className={styles.patientName}>{a.patient}</span>
                            </div>
                          </div>

                          <div className={styles.cell}>{a.clinician}</div>

                          <div className={styles.cell}>
                            <span
                              className={`${styles.badge} ${
                                a.consentStatus === "EMAIL_BOUNCED"
                                  ? styles.badgeBounced
                                  : a.consentStatus === "NOT_SIGNED"
                                  ? styles.badgeNotSigned
                                  : styles.badgeSigned
                              }`}
                            >
                              {a.consentStatus === "EMAIL_BOUNCED"
                                ? "EMAIL BOUNCED"
                                : a.consentStatus === "NOT_SIGNED"
                                ? "NOT SIGNED"
                                : "SIGNED"}
                            </span>
                          </div>

                          <div className={styles.cell}>
                            <div className={styles.chip}><FiMail /><span>{a.emails}</span></div>
                          </div>

                          <div className={styles.cell}>
                            <div className={styles.chip}><FiMessageCircle /><span>{a.texts}</span></div>
                          </div>

                          <div className={`${styles.cell} ${styles.actions}`}>
                            <button className={`${styles.btn} ${styles.btnLight}  tutorial-qr`} onClick={() => openQr(a)}>
                              <FiGrid /> QR Code
                            </button>
                           
                            {a.consentStatus !== "SIGNED" ? (
                              <button className={`${styles.btn} ${styles.btnPill}`}>Update email</button>
                            ) : (
                              <button className={`${styles.btn} ${styles.btnPill} ${styles.btnWhite}`}>Email</button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className={styles.tableFooter}>
                    <div className={styles.pagerInfo}>
                      Page {page} • Per page
                      <select
                        value={perPage}
                        onChange={(e) => { setPage(1); setPerPage(parseInt(e.target.value, 10)); }}
                        style={{ marginLeft: 8 }}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </select>
                      <span style={{ marginLeft: 12 }}>Total: {total}</span>
                    </div>
                    <div className={styles.pagerBtns}>
                      <button
                        className={styles.btn}
                        disabled={page <= 1 || loadingToday}
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                      >
                        Prev
                      </button>
                      <button
                        className={styles.btn}
                        disabled={loadingToday || (todayAppts.length < perPage && page * perPage >= total)}
                        onClick={() => setPage(p => p + 1)}
                        style={{ marginLeft: 8 }}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* ---- Send reminder ---- */}
            {activeTab === "reminders" && (
              <>
                <h4 className={styles.sectionTitle}>Send reminders</h4>

                <div className={styles.table}>
                  <div className={`${styles.gridRem} ${styles.tableHead}`}>
                    <div>
                       <Tooltip title="Select all items">
                        <div className={styles.checkboxRow}>
                          <input
                            type="checkbox"
                            checked={remindersFiltered.length > 0 && remindersFiltered.every(r => reminderSelected.has(r.id))}
                            onChange={toggleReminderAll}
                          />
                          <span className={styles.checkboxLabel}></span>
                        </div>
                       </Tooltip>
                    </div>
                    <div>Date / Time</div>
                    <div>Patient name</div>
                    <div>Clinician</div>
                    <div>Days until</div>
                    <div>Consent status</div>
                    <div>Reminders</div>
                    <div>Last reminder</div>
                    <div>Actions</div>
                  </div>

                  <div className={styles.tableBody}>
                    {loadingRem && (
                      <div className={styles.row}>
                        <div className={styles.cell} style={{ gridColumn: "1 / -1" }}>
                          Loading future appointments…
                        </div>
                      </div>
                    )}

                    {errorRem && !loadingRem && (
                      <div className={styles.row}>
                        <div className={styles.cell} style={{ gridColumn: "1 / -1", color: "crimson" }}>
                          {errorRem}
                        </div>
                      </div>
                    )}

                    {!loadingRem && !errorRem && remindersFiltered.map((r) => {
                      const dateObj = r.date ? new Date(r.date) : null;
                      const sel = reminderSelected.has(r.id);

                      return (
                        <div className={`${styles.rowSoft} ${styles.gridRem}`} key={r.id}>
                          <div className={styles.cell}>
                            <input type="checkbox" checked={sel} onChange={() => toggleReminderRow(r.id)} />
                          </div>

                          <div className={styles.cell}>
                            <div className={styles.dateBlock}>
                              <div className={styles.dateLine}><FiCalendar /> {dateObj ? fmtShort(dateObj) : "-"}</div>
                              <div className={styles.timeLine}><FiClock /> {r.time}</div>
                            </div>
                          </div>

                          <div className={styles.cell}>
                            <div className={styles.patient}>
                              <span className={styles.avatar}>
                                {r.patient.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()}
                              </span>
                              <span className={styles.patientName}>{r.patient}</span>
                            </div>
                          </div>

                          <div className={styles.cell}>{r.clinician}</div>

                          <div className={styles.cell}>
                            <span className={`${styles.pill} ${styles.pillDays}`}>{r.days_until_appointment ?? r.daysUntilAppointment ?? 0} DAYS</span>
                          </div>

                          <div className={styles.cell}>
                            {r.status?.toLowerCase() === "draft" ? (
                              <span className={`${styles.badge} ${styles.badgeGray}`}>DRAFT</span>
                            ) : r.status?.toLowerCase().includes("sign") ? (
                              <span className={`${styles.badge} ${styles.badgeYellow}`}>SENT FOR SIGNING</span>
                            ) : (
                              <span className={`${styles.badge} ${styles.badgeNotSigned}`}>{r.status ?? "UNKNOWN"}</span>
                            )}
                          </div>

                          <div className={styles.cell}>
                            <div className={styles.iconWithNum}><FiMail /><span>{r.reminders ?? 0}</span></div>
                          </div>

                          <div className={styles.cell}>
                            {r.lastReminder ? fmtShort(new Date(r.lastReminder)) : "Never"}
                          </div>

                          <div className={`${styles.cell} ${styles.actions}`} style={{ display: "flex", gap: 1, alignItems: "center" }}>
                            <button
                              className={`${styles.btn} ${styles.btnOutlineBlue}`}
                              onClick={() => {
                                const id = Number(r.raw?.id ?? r.id);
                                sendReminders([id]);
                              }}
                            >
                              <FiSend /> Send reminder
                            </button>
                            <button
                              className={styles.iconBtn}
                              title="Archive"
                              onClick={() => askArchive(r.id, r.patient, "reminders")}
                              style={{ border: "none", background: "transparent", cursor: "pointer", padding: 3 }}
                            >
                              <FiArchive />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className={styles.bulkBar}>
                  <button
                    className={styles.bulkBtn}
                    disabled={reminderSelected.size === 0}
                    onClick={doBulkReminders}
                  >
                    Bulk send
                  </button>
                </div>

              </>
            )}

            {/* ---- Ready for upload ---- */}
            {activeTab === "upload" && (
              <>
                <h4 className={styles.sectionTitle}>Ready for upload</h4>

                <div className={styles.table}>
                  <div className={`${styles.gridUpload} ${styles.tableHead}`}>
                    <div>
                     
                     <Tooltip title="Select all items">
                      <div className={styles.checkboxRow}>
                        <input
                          type="checkbox"
                          checked={uploadFiltered.length > 0 && uploadFiltered.every(r => uploadSelected.has(r.id))}
                          onChange={toggleUploadAll}
                        />
                        <span className={styles.checkboxLabel}></span>
                      </div>
                    </Tooltip>
                    </div>
                    <div>Patient name</div>
                    <div>Appointment date</div>
                    <div>Clinician</div>
                    {/* <div>Consent Status</div> */}
                    <div>Status</div>
                    <div>Email</div>
                    <div>SMS</div>
                    <div>Signed date</div>
                    <div>Actions</div>
                  </div>

                  <div className={styles.tableBody}>
                    {loadingUpload && (
                      <div className={styles.row}>
                        <div className={styles.cell} style={{ gridColumn: "1 / -1" }}>
                          Loading ready-for-upload…
                        </div>
                      </div>
                    )}

                    {errorUpload && !loadingUpload && (
                      <div className={styles.row}>
                        <div className={styles.cell} style={{ gridColumn: "1 / -1", color: "crimson" }}>
                          {errorUpload}
                        </div>
                      </div>
                    )}

                    {!loadingUpload && !errorUpload && uploadFiltered.map((u) => {
                      const sel = uploadSelected.has(u.id);
                      const completed = u.raw?.uploaded_to_dentally_patient_record === true || String(u.status).toLowerCase().includes("completed");

                      return (
                        <div className={`${styles.gridUpload} ${completed ? styles.rowUploadCompleted : styles.rowUpload}`} key={u.id}>
                          <div className={styles.cell}>
                            <input type="checkbox" checked={sel} onChange={() => toggleUploadRow(u.id)} />
                          </div>

                          <div className={styles.cell}>
                            <div className={styles.patient}>
                              <span className={styles.avatar}>
                                {u.patient.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()}
                              </span>
                              <span className={styles.patientName}>{u.patient}</span>
                            </div>
                          </div>

                          <div className={styles.cell}>{fmtShort(new Date(u.appointmentDate))}</div>
                          <div className={styles.cell}>{u.clinician}</div>

                          <div className={styles.cell}>
                            {(() => {
                              const uploaded = Boolean(u.raw?.uploaded_to_dentally_patient_record);
                              const cls = getUploadStatusBadgeClass(u.status, uploaded);
                              return (
                                <span className={`${styles.badge} ${styles[cls]}`}>
                                  {uploaded ? "COMPLETED" : (u.status || "UNKNOWN")}
                                </span>
                              );
                            })()}
                          </div>

                          <div className={styles.cell}>
                            <div className={styles.iconWithNum}><FiMail /><span>{u.email}</span></div>
                          </div>

                          <div className={styles.cell}>
                            <div className={styles.iconWithNum}><FiMessageCircle /><span>{u.sms}</span></div>
                          </div>

                          <div className={styles.cell}>
                            {u.signedDate ? fmtShort(new Date(u.signedDate)) : "Not signed"}
                          </div>

                          <div className={styles.cell} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <button
                              className={styles.iconBtn}
                              title="Archive"
                              onClick={() => askArchive(u.raw?.id ?? u.id, u.patient, "upload")}
                              style={{ border: "none", background: "transparent", cursor: "pointer", padding: 6 }}
                            >
                              <FiArchive />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className={styles.uploadFooter}>
                  <div className={styles.uploadCount}>
                    {uploadSelected.size} of {uploadFiltered.length} items selected
                  </div>
                 <div className={styles.uploadBtns}>
                    <button
                      className={styles.btnSecondary}
                      disabled={uploadSelected.size === 0}
                      onClick={doBulkArchiveUpload}
                    >
                      Archive selected
                    </button>
                    <button
                      className={styles.btnPrimary}
                      disabled={uploadSelected.size === 0}
                      onClick={doBulkDownload}
                    >
                      Download
                    </button>
                  </div>
                </div>

                <div className={styles.tableFooter}>
                  <div className={styles.pagerInfo}>
                    Page {pageUpload} • Per page
                    <select
                      value={perPageUpload}
                      onChange={(e) => { setPageUpload(1); setPerPageUpload(parseInt(e.target.value, 10)); }}
                      style={{ marginLeft: 8 }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                    <span style={{ marginLeft: 12 }}>Total: {totalUpload}</span>
                  </div>
                  <div className={styles.pagerBtns}>
                    <button
                      className={styles.btn}
                      disabled={pageUpload <= 1 || loadingUpload}
                      onClick={() => setPageUpload(p => Math.max(1, p - 1))}
                    >
                      Prev
                    </button>
                    <button
                      className={styles.btn}
                      disabled={loadingUpload || (pageUpload * perPageUpload >= totalUpload)}
                      onClick={() => setPageUpload(p => p + 1)}
                      style={{ marginLeft: 8 }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
            
          </div>
        </section>

        <footer className={styles.footerStripe} />
      </div>



      {/* {showTutorial && (
        <TutorialOverlay steps={steps} onClose={() => setShowTutorial(false)} />
      )} */}


{notify.show && (
  <div
    style={{
      position: "fixed",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      background: "#fff",
      padding: "24px 32px",
      borderRadius: 12,
      boxShadow: "0 12px 30px rgba(0,0,0,0.25)",
      zIndex: 99999,
      textAlign: "center",
      minWidth: 180,
    }}
  >
    {notify.type === "archive" && (
      <div style={{ fontSize: 48, marginBottom: 8, animation: "binAnim 1.5s ease" }}>
        🗑️
      </div>
    )}

    {notify.type === "download" && (
      <div style={{ fontSize: 40, marginBottom: 8, animation: "spin 1s linear infinite" }}>
        ⬇️
      </div>
    )}

    {notify.type === "success" && (
      <div style={{ fontSize: 40, marginBottom: 8, color: "green" }}>
        ✅
      </div>
    )}

    <div style={{ fontWeight: 600 }}>{notify.message}</div>
  </div>
)}

      {/* QR Modal */}
      <QrModal open={qrOpen} appt={selectedAppt} onClose={() => setQrOpen(false)} />

      {/* --- Archive Confirmation (inline styles so it always shows nicely) --- */}
      {confirmArchive.open && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999
          }}
        >
          <div
            style={{
              background: "#fff", borderRadius: 12, padding: 24, width: "min(420px, 92vw)",
              boxShadow: "0 20px 60px rgba(0,0,0,0.25)"
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: 8 }}>Archive consent bundle</h3>
            <p style={{ marginTop: 0, opacity: 0.8 }}>
              Are you sure you want to archive{confirmArchive.name ? ` “${confirmArchive.name}”` : ""}?
            </p>

            <div style={{ display: "flex", gap: 12, marginTop: 20, justifyContent: "flex-end" }}>
              <button
                className={styles.btn}
                onClick={() => setConfirmArchive({ open: false, source: null, loading: false })}
                disabled={confirmArchive.loading}
              >
                Cancel
              </button>
              <button
                className={`${styles.btn} ${styles.btnOutlineBlue}`}
                onClick={doArchive}
                disabled={confirmArchive.loading}
              >
                {confirmArchive.loading ? "Archiving…" : "Archive"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Dashboard;
